import type { ValidatorFn } from "./rules";
import type { IntakeFormData } from "../types";
import { TOTAL_STEPS } from "../types";
import { stepSchemas } from "./schemas";

export interface ValidationResult {
  errors: Record<string, string>;
  isValid: boolean;
}

/**
 * Run all validators for a single field, returning the first error or null.
 */
export function validateField(
  value: unknown,
  validators: ValidatorFn[],
): string | null {
  for (const validate of validators) {
    const error = validate(value);
    if (error) return error;
  }
  return null;
}

/**
 * Validate all fields for a given step.
 * Returns { errors: { fieldName: errorMessage }, isValid: boolean }
 */
export function validateStep(
  step: number,
  state: IntakeFormData,
): ValidationResult {
  const schema = stepSchemas[step];
  if (!schema) return { errors: {}, isValid: true };

  // Resolve schema — it may be a function of form state
  const fields = typeof schema === "function" ? schema(state) : schema;

  const errors: Record<string, string> = {};
  for (const [field, validators] of Object.entries(fields)) {
    // Support dot-notation for nested fields (e.g., "documents.wageTax")
    const value = field.split(".").reduce<unknown>(
      (obj, key) =>
        obj != null && typeof obj === "object" ? (obj as Record<string, unknown>)[key] : undefined,
      state,
    );
    const error = validateField(value, validators);
    if (error) {
      errors[field] = error;
    }
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}

/**
 * Steps whose persisted data fully satisfies their validation schema.
 *
 * Used to seed the progress bar's "complete" (checkmark) state on load so it is
 * derived from the saved form data rather than from which pages happened to be
 * visited in the current browser session — letting completion survive across
 * sessions and across different users on the same form (ADO-3452).
 *
 * A step whose resolved schema has no fields of its own (e.g. Review) can't be
 * "complete" in isolation; it is reported complete only once every data-bearing
 * step is complete — i.e. the form is review-ready, so a submitted/corrected
 * version shows every page with a green checkmark.
 */
export function computeCompletedSteps(state: IntakeFormData): Set<number> {
  const completed = new Set<number>();
  const dataSteps: number[] = [];

  for (let step = 1; step <= TOTAL_STEPS; step++) {
    const schema = stepSchemas[step];
    const fields = !schema ? {} : typeof schema === "function" ? schema(state) : schema;
    if (Object.keys(fields).length === 0) continue; // no requirements of its own
    dataSteps.push(step);
    if (validateStep(step, state).isValid) completed.add(step);
  }

  // Whole form is review-ready → mark the schema-less steps (Review) complete too.
  if (dataSteps.length > 0 && dataSteps.every((s) => completed.has(s))) {
    for (let step = 1; step <= TOTAL_STEPS; step++) completed.add(step);
  }

  return completed;
}

/**
 * Fields that should always be saved regardless of validation state.
 * These are either:
 * - JSON blob fields (owners, affiliatedGroups) that hold partial data
 * - Tri-state toggles where null is a valid save value (unanswered)
 * - Conditional sub-fields that should save even if the parent toggle is unanswered
 */
const ALWAYS_SAVE_FIELDS = new Set([
  "owners", "affiliatedGroups", "ownershipAssignments",
  "hasCba", "hasPeo", "isMultiEmployer", "isCommonlyControlledGroup",
  "cbaUnitName", "cbaEffectiveDate", "peoType",
  "multiEmployerType",
  "docVerificationType", "newlyFormedCompany", "ownerEligibilityDocs",
]);

/**
 * Build a copy of the form data with any field that fails its validators
 * removed. Used by the auto-save path: the AC says fields needing attention
 * (bad/empty/format-invalid) must NOT be persisted while their value is
 * invalid — the previous-good value stays in SF until the user types a
 * passing value.
 *
 * Fields in ALWAYS_SAVE_FIELDS are exempt — they hold partial/incomplete
 * data that should persist across saves (e.g., partial owner rows, unanswered
 * toggles). Only format-validated fields (EIN, zip, SIC, etc.) get stripped.
 *
 * Applies validators across every step's schema, not just the current one,
 * so an invalid value entered on step 1 stays out of the auto-save even if
 * the user has already navigated forward.
 */
export function stripInvalidFieldsFromSnapshot(
  state: IntakeFormData,
): Partial<IntakeFormData> {
  const result: Record<string, unknown> = { ...(state as unknown as Record<string, unknown>) };
  // Employees are saved via the dedicated saveEmployees() path — exclude from
  // the navigation save to prevent concurrent SaveCensusMembersAsync races.
  delete result.employees;
  for (const schema of Object.values(stepSchemas)) {
    const fields = typeof schema === "function" ? schema(state) : schema;
    for (const [field, validators] of Object.entries(fields)) {
      if (ALWAYS_SAVE_FIELDS.has(field)) continue;
      const value = field.split(".").reduce<unknown>(
        (obj, key) =>
          obj != null && typeof obj === "object" ? (obj as Record<string, unknown>)[key] : undefined,
        state,
      );
      if (validateField(value, validators)) {
        delete result[field];
      }
    }
  }
  return result as Partial<IntakeFormData>;
}
